/**
 * seamDigest - a chunk mesh's BORDER, indexed once instead of re-scanned per question.
 *
 * Every question the seam path asks of a chunk is a question about its border: which cells of a face
 * layer hold a vertex, which vertex sits in a given rim cell, which triangles touch the ring the mask
 * collapses. Each was answered by a full sweep of `cellIndices` — one entry per EXPANDED vertex, so
 * three per triangle — and each sweep recovered the cell's (x, y, z) with three integer divisions,
 * real ones because GRID_SIZE is 34 and not a power of two. One owner rebuild ran ~130 such sweeps
 * over 5k-40k entries apiece, which is where the seam flush's frame time went.
 *
 * The digest is a PURE FUNCTION of `cellIndices` and the mesh's PRISTINE index buffer, and it is
 * cached against the `cellIndices` ARRAY rather than against the chunk: expandGeometry allocates a
 * fresh one per mesh (with the index buffer beside it) and nothing ever rewrites one in place, so a
 * cache miss is exactly a remesh and there is no invalidation to get wrong. The band mask does not
 * invalidate it — that rewrites the LIVE indices, and the digest reads the pristine snapshot
 * (indexRestore), which is also what makes `sideTris` a superset whatever has since been collapsed.
 *
 * The mesh is indexed with SHARED vertices (MeshGeometry): triangle t's corners are whatever the
 * index buffer says, not 3t..3t+2. `buildTris` used to derive them from the vertex order alone,
 * which was exactly the identity-permutation assumption; with a real index buffer it classified
 * triangles by three unrelated vertices, so the owner never found the ring to mask and the seam never
 * found the triangles near a face to bridge.
 */

import { GRID_SIZE, CHUNK_SIZE } from '@worldify/shared';
import type { ChunkGeometry } from './ChunkGeometry.js';
import { pristineIndices } from './indexRestore.js';

const GRID = GRID_SIZE;
/**
 * THE OUTERMOST CELL ON THE HIGH SIDE — the last cell marched, the far edge of the margin. Both rim
 * tables are indexed INWARD FROM THE CHUNK'S EDGE on their side: slot 0 is cell 0 on the low side and
 * this cell on the high side. That is what makes the two tables mirrors, and it tracks MESH_MARGIN on
 * its own — every extra margin layer moves this edge outward and the numbering follows.
 */
export const EDGE_HIGH_CELL = GRID - 2;

/**
 * THE SHARED RING'S CELL INDEX, and the plane's position is measured from it — NOT from GRID.
 *
 * A chunk owns voxels 0..CHUNK_SIZE-1 and borrows the rest, so its cell CHUNK_SIZE is the first cell
 * built entirely from the neighbour's voxels: the same cell as the neighbour's cell 0, over the same
 * eight voxels. That is the ring ON the plane, and it sits at CHUNK_SIZE whatever MESH_MARGIN is.
 *
 * GRID-relative constants happen to agree at MESH_MARGIN 2 and silently drift the moment it changes —
 * every extra margin voxel moves GRID but not the plane. Anchoring here so the two cannot diverge.
 */
const RING = CHUNK_SIZE;

/**
 * THE BORDER CELL ON THE LOW SIDE OF A PLANE — cell GRID-2 for the chunk below it, cell 0 for the one
 * above. Exported so the seam's rim lookups and this file's slot numbering cannot drift: slot s of a
 * high-side rim table is cell `BORDER_LOW_CELL - s`.
 *
 * GRID-2, NOT GRID-3, AND THE DIFFERENCE WAS A WHOLE CELL OF SEAM.
 *
 * Cells tile: chunk A's cell GRID-2 and its neighbour B's cell 0 are the SAME cell over the SAME
 * eight voxels — A reaches them through its high-side margin, B holds them natively, and both compute
 * the identical vertex. That shared cell is the ring ON the plane, and it is what a seam must anchor
 * to from either side.
 *
 * At GRID-3 the low side anchored on the shared ring (cell 0) and the high side anchored on the ring
 * BEFORE it — the cell that merely straddles the boundary. So every stitch driven from a chunk below
 * a plane had to span one extra cell of its own level, and where the surface only grazed that cell
 * there was nothing to span to. That is the whole of "far more gaps on the fine chunk's high side",
 * and it is why the two signs of face behaved differently for a reason that had nothing to do with
 * sign: one of them was simply reading the wrong row.
 *
 * The ring is real geometry, not extrapolation: cell GRID-2's voxels come from the + neighbour at the
 * same level, which is resident whenever this chunk is meshed (every level's load set is its in-range
 * set unioned with the positive-margin dilation) even though it is usually not displayed.
 */
export const RING_CELL = RING;

/**
 * The high-side rim table's origin. Slot s is cell `BORDER_LOW_CELL - s`, so this has to be the EDGE,
 * not the ring: with margin layers past the plane, a table numbered from the ring gives the cells
 * outside it no slot at all — `rimVertex` computes a negative index and returns undefined, so the
 * outermost geometry is unreachable and the seam anchors somewhere inside the surface it should be
 * bridging from.
 */
export const BORDER_LOW_CELL = EDGE_HIGH_CELL;

/**
 * How far inward of the plane a rim probe may reach — the longest of the four cell lists.
 *
 * FOUR, because a DRIVER and a PARTNER start on different rows. A driver anchors on the shared ring
 * itself; a partner anchors one row IN, because the ring it would otherwise use is the one it gives up
 * for the stitch to replace. So the deepest list is a partner's three rows starting one in, and the
 * table has to hold cells 0..3 low and GRID-2..GRID-5 high.
 */
export const RIM_DEPTHS = EDGE_HIGH_CELL - (RING - 3) + 1;

/**
 * THE AXIS-CELL VALUES THE SEAM PATH CAN NAME, and the only ones the band index carries.
 *
 * Low side: the rim reads cells 0..3 and the ring mask reads cell 0. High side: the rim reads
 * RING..RING-3 and the mask reads RING..RING+1 (`c > RING - 1`, from the shared plane outward).
 * Five per side covers both, and the two bands do not meet at any supported margin.
 */
export const BAND_DEPTH = 5;
/**
 * The high half runs from three cells inward of the ring out to the LAST MARCHED CELL, computed rather
 * than listed. Every extra margin voxel adds a cell past the plane, and a cell the band does not name
 * has no slot — so `bandStart` returns -1 for it, the rim cannot see it, and the outermost ring of
 * geometry becomes invisible to the seam with no error anywhere. A fixed list silently loses the new
 * ring the moment MESH_MARGIN changes, which is exactly the coupling the RING anchor exists to remove.
 */
const BAND_CELLS: readonly number[] = (() => {
  const out: number[] = [];
  for (let c = 0; c < RIM_DEPTHS + 1; c++) out.push(c);
  for (let c = RING - 3; c <= GRID - 2; c++) out.push(c);
  return out;
})();
const BAND_SLOTS = BAND_CELLS.length;

/** Cell -> its slot in a band CSR, or -1 for the interior the seam never asks about. */
const SLOT_OF_CELL = ((): Int8Array => {
  const m = new Int8Array(GRID).fill(-1);
  for (let s = 0; s < BAND_SLOTS; s++) m[BAND_CELLS[s]] = s;
  return m;
})();

export interface SeamDigest {
  /** Vertices described — `cellIndices.length`, one per shared mesh vertex. */
  readonly count: number;
  /**
   * Per vertex, its mesh-grid cell's (x, y, z), interleaved at `v * 3`. A byte each because GRID is
   * 34, and interleaved because every consumer that wants one component wants at least two — one
   * cache line answers all three.
   */
  readonly comps: Uint8Array;
  /**
   * Per axis, the vertices whose `axis` cell coordinate lies in the band, grouped by cell. Slot s is
   * `[bandOffsets[axis][s], bandOffsets[axis][s+1])` and covers cell `BAND_CELLS[s]`; within a group
   * the vertices are ASCENDING, which is what makes a first-wins fill pick the same vertex the old
   * cell->vertex Map did.
   */
  readonly bandVerts: readonly Uint32Array[];
  readonly bandOffsets: readonly Uint32Array[];
  /**
   * Per (axis, side), the TRIANGLES with a corner in that side's border band, ascending — indexed
   * `axis * 2 + (low ? 0 : 1)`. See `sideTris`.
   */
  readonly tris: readonly Uint32Array[];
  /** Lazily-built rim tables, indexed `axis * 2 + (low ? 0 : 1)`. See `rimTable`. */
  readonly rims: (Int32Array | null)[];
}

const cache = new WeakMap<Uint16Array, SeamDigest>();

/**
 * The digest for one layer's per-vertex cell indices and PRISTINE triangle list, built on first ask
 * and kept with the cell array. Prefer `digestFor`, which fetches both from the chunk.
 */
export function seamDigest(cellIndices: Uint16Array, indices: ArrayLike<number>): SeamDigest {
  const got = cache.get(cellIndices);
  if (got !== undefined) return got;
  const made = build(cellIndices, indices);
  cache.set(cellIndices, made);
  return made;
}

/** ...and for a layer of a chunk, or null when the layer is empty or has no index buffer. */
export function digestFor(geo: ChunkGeometry, layer: number): SeamDigest | null {
  const cells = geo.getCellIndices(layer);
  const g = geo.getLayerGeometry(layer);
  if (!cells || !g) return null;
  const indices = pristineIndices(g);
  return indices ? seamDigest(cells, indices) : null;
}

/**
 * HOW MANY VERTICES SIT IN ONE AXIS CELL LAYER — the count the border-surface test used to reach by
 * scanning the whole array. Zero for a cell outside the band, which no caller names.
 */
export function bandCount(d: SeamDigest, axis: number, cell: number): number {
  const s = SLOT_OF_CELL[cell];
  if (s < 0) return 0;
  const off = d.bandOffsets[axis];
  return off[s + 1] - off[s];
}

/** Start of one axis cell layer's vertex group in `bandVerts[axis]`; -1 for a non-band cell. */
export function bandStart(d: SeamDigest, axis: number, cell: number): number {
  const s = SLOT_OF_CELL[cell];
  return s < 0 ? -1 : d.bandOffsets[axis][s];
}

/** End of the group `bandStart` returns. */
export function bandEnd(d: SeamDigest, axis: number, cell: number): number {
  const s = SLOT_OF_CELL[cell];
  return s < 0 ? -1 : d.bandOffsets[axis][s + 1];
}

/**
 * THE TRIANGLES TOUCHING ONE SIDE'S BORDER BAND, so the two walks that edit or read a chunk's index
 * buffer stop visiting every triangle in it to find the ~20% near a face.
 *
 * Derived from the PRISTINE index buffer (see the file header), so it describes every triangle as
 * built whether or not the band mask has since collapsed it — the same snapshot `restoreBands`
 * restores from. It is a SUPERSET for any mask narrower than BAND_DEPTH: the caller's own per-corner
 * band test stays the authority, this only skips triangles that cannot pass it.
 */
export function sideTris(d: SeamDigest, axis: number, low: boolean): Uint32Array {
  return d.tris[axis * 2 + (low ? 0 : 1)];
}

/**
 * THE RIM TABLE: `(slot, c1, c2) -> lowest vertex index there`, dense, -1 where nothing sits.
 *
 * This replaces the per-chunk `Map<cellIndex, vertex>` the seam built on every build — a
 * hash probe and a hash insert for every one of the mesh's expanded vertices, to answer lookups that
 * only ever name the six border cells. Slot s is cell `s` on the low side and `BORDER_LOW_CELL - s`
 * on the high one; `c1` is the `(axis+1)%3` coordinate and `c2` the `(axis+2)%3` one.
 *
 * Built on demand per (axis, side) and kept with the digest, so a settled world builds none and a
 * chunk that stays a seam participant across flushes builds each of its at most six exactly once.
 */
export function rimTable(d: SeamDigest, axis: number, low: boolean): Int32Array {
  const slot = axis * 2 + (low ? 0 : 1);
  const have = d.rims[slot];
  if (have !== null) return have;
  const tab = new Int32Array(RIM_DEPTHS * GRID * GRID).fill(-1);
  const t1 = (axis + 1) % 3, t2 = (axis + 2) % 3;
  const verts = d.bandVerts[axis];
  const comps = d.comps;
  for (let s = 0; s < RIM_DEPTHS; s++) {
    const cell = low ? s : BORDER_LOW_CELL - s;
    const from = bandStart(d, axis, cell), to = bandEnd(d, axis, cell);
    if (from < 0) continue;
    const base = s * GRID * GRID;
    for (let i = from; i < to; i++) {
      const v = verts[i];
      const b = v * 3;
      const at = base + comps[b + t1] * GRID + comps[b + t2];
      if (tab[at] < 0) tab[at] = v;
    }
  }
  d.rims[slot] = tab;
  return tab;
}

function build(cellIndices: Uint16Array, indices: ArrayLike<number>): SeamDigest {
  const count = cellIndices.length;
  const comps = new Uint8Array(count * 3);
  const bandOffsets: Uint32Array[] = [];
  const bandVerts: Uint32Array[] = [];
  for (let axis = 0; axis < 3; axis++) {
    bandOffsets.push(new Uint32Array(BAND_SLOTS + 1));
    bandVerts.push(new Uint32Array(0));
  }
  for (let v = 0; v < count; v++) {
    const c = cellIndices[v];
    const x = c % GRID;
    const r = (c - x) / GRID;
    const y = r % GRID;
    const z = (r - y) / GRID;
    const b = v * 3;
    comps[b] = x;
    comps[b + 1] = y;
    comps[b + 2] = z;
    // Counting pass, one axis at a time: offsets[s+1] holds the tally for slot s.
    const sx = SLOT_OF_CELL[x]; if (sx >= 0) bandOffsets[0][sx + 1]++;
    const sy = SLOT_OF_CELL[y]; if (sy >= 0) bandOffsets[1][sy + 1]++;
    const sz = SLOT_OF_CELL[z]; if (sz >= 0) bandOffsets[2][sz + 1]++;
  }
  const cursor: Uint32Array[] = [];
  for (let axis = 0; axis < 3; axis++) {
    const off = bandOffsets[axis];
    for (let s = 0; s < BAND_SLOTS; s++) off[s + 1] += off[s];
    bandVerts[axis] = new Uint32Array(off[BAND_SLOTS]);
    cursor.push(off.slice(0, BAND_SLOTS));
  }
  // Ascending v, so each group comes out ascending and a first-wins fill picks the lowest index.
  for (let v = 0; v < count; v++) {
    const b = v * 3;
    for (let axis = 0; axis < 3; axis++) {
      const s = SLOT_OF_CELL[comps[b + axis]];
      if (s >= 0) bandVerts[axis][cursor[axis][s]++] = v;
    }
  }
  return {
    count, comps, bandVerts, bandOffsets,
    tris: buildTris(comps, indices),
    rims: [null, null, null, null, null, null],
  };
}

/** Where the high band starts — anchored to the ring, so an extra margin voxel does not shift it off
 *  the deepest cell BAND_CELLS names and silently drop that row's triangles. */
const HIGH_FROM = RING - 3;

function buildTris(comps: Uint8Array, indices: ArrayLike<number>): Uint32Array[] {
  // Triangle t's corners are indices[3t..3t+2] — the mesh is indexed with shared vertices.
  const triCount = (indices.length / 3) | 0;
  const sizes = new Uint32Array(6);
  for (let t = 0; t < triCount; t++) {
    const a = t * 3;
    const b0 = indices[a] * 3, b1 = indices[a + 1] * 3, b2 = indices[a + 2] * 3;
    for (let axis = 0; axis < 3; axis++) {
      const c0 = comps[b0 + axis], c1 = comps[b1 + axis], c2 = comps[b2 + axis];
      if (c0 < BAND_DEPTH || c1 < BAND_DEPTH || c2 < BAND_DEPTH) sizes[axis * 2]++;
      if (c0 >= HIGH_FROM || c1 >= HIGH_FROM || c2 >= HIGH_FROM) sizes[axis * 2 + 1]++;
    }
  }
  const out: Uint32Array[] = [];
  for (let i = 0; i < 6; i++) out.push(new Uint32Array(sizes[i]));
  const cursor = new Uint32Array(6);
  for (let t = 0; t < triCount; t++) {
    const a = t * 3;
    const b0 = indices[a] * 3, b1 = indices[a + 1] * 3, b2 = indices[a + 2] * 3;
    for (let axis = 0; axis < 3; axis++) {
      const c0 = comps[b0 + axis], c1 = comps[b1 + axis], c2 = comps[b2 + axis];
      if (c0 < BAND_DEPTH || c1 < BAND_DEPTH || c2 < BAND_DEPTH) {
        out[axis * 2][cursor[axis * 2]++] = t;
      }
      if (c0 >= HIGH_FROM || c1 >= HIGH_FROM || c2 >= HIGH_FROM) {
        out[axis * 2 + 1][cursor[axis * 2 + 1]++] = t;
      }
    }
  }
  return out;
}
